import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import axios from 'src/utils/axios';
import {
  CertHouseholdData,
  CertMember,
  CertificationProperty,
} from 'src/types/Certification';
import { fadeStyle } from 'src/components/NewRenterInterface/constants';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import {
  setElevatedAuth,
  selectIsTotpElevated,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import useElevatedAuthExpiryWatcher from 'src/hooks/useElevatedAuthExpiryWatcher';
import { formatPhoneNumber } from 'src/utils/InputValidationUtils';
import { fieldErrorFromPayload } from 'src/utils/errors/contactErrorMessages';
import { getRoleColors, isMinorMemberType } from '../memberRoleColors';
import { findMemberContactConflict } from './householdContactUtils';
import MemberContactEditor from './MemberContactEditor';
import TwoFactorRequiredCard from './TwoFactorRequiredCard';
import AccountContactsEditor from 'src/components/NewRenterInterface/AccountHub/AccountContactsEditor';
import elevatedAuthService from 'src/services/elevatedAuth.service';
import HoHTransferDialog, {
  type MemberType,
} from '../HouseholdComposition/HoHTransferDialog';
import HoHDemotionDialog, {
  type HouseholdMemberOption,
} from '../HouseholdComposition/HoHDemotionDialog';

interface EditHouseholdMemberScreenProps {
  household: CertHouseholdData;
  member: CertMember;
  property?: CertificationProperty;
  onSubmit: (updatedMembers: CertMember[]) => void;
  onCancel: () => void;
  onDelete?: (member: CertMember) => void;
  sx?: SxProps;
}

interface FormValues {
  first_name: string;
  middle_name: string;
  last_name: string;
  member_type: MemberType | null;
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  member_type?: string;
}

const EditHouseholdMemberScreen: FC<EditHouseholdMemberScreenProps> = ({
  household,
  member,
  onSubmit,
  onCancel: _onCancel,
  onDelete,
  sx = {},
}) => {
  const dispatch = useDispatch();
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  const hasOtherMembers = (household.cert_members ?? []).some(
    (m) => m.cert_member_id !== member.cert_member_id
  );

  const currentUserMember = (household.cert_members ?? []).find(
    (m) => m.user_id === user?.id
  );

  const currentUserCanManage =
    currentUserMember?.is_head_of_household === true ||
    currentUserMember?.is_helper === true;

  const [memberTypes, setMemberTypes] = useState<MemberType[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [alertMessage, setAlertMessage] = useState<{
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
  } | null>(null);

  const [formValues, setFormValues] = useState<FormValues>({
    first_name: member.first_name ?? '',
    middle_name: member.middle_name ?? '',
    last_name: member.last_name ?? '',
    member_type: member.member_type_id
      ? {
          id: member.member_type_id,
          name: member.member_type_name,
          display_name: member.member_type_name,
          weight: member.member_type_weight ?? 0,
        }
      : null,
  });

  const [showHoHTransferDialog, setShowHoHTransferDialog] = useState(false);
  const [showHoHDemotionDialog, setShowHoHDemotionDialog] = useState(false);
  const [transferDemotedRole, setTransferDemotedRole] =
    useState<MemberType | null>(null);
  const [demotionNewHoH, setDemotionNewHoH] =
    useState<HouseholdMemberOption | null>(null);

  // Contact Information (State A — member has no contact yet). Saved as part of
  // the main Save Changes action, not via a separate button.
  // Only consider the member as having contact info if there is a *primary*
  // email or phone. Non-primary contacts are treated as "not yet set up" so
  // the user can enter the address and the backend will promote it to primary.
  const hasContact =
    (member.emails ?? []).some(
      (e) => e.is_primary && !e.email.endsWith('@prontohousing.io')
    ) || (member.phones ?? []).some((p) => p.is_primary);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  // State B — member already has contact info.
  //  - elevated (TOTP) session available → render only the contact editor
  //  - no authenticator configured → read-only info + disabled Edit + blocker
  //  - authenticator configured but not elevated → read-only info + Edit,
  //    which opens the Verify Your Identity modal
  const [showContactEditor, setShowContactEditor] = useState(false);
  const elevatedAuth = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.accountHub.elevatedAuth
  );
  const isElevated = useSelector(selectIsTotpElevated);
  const totpEnabled = elevatedAuth.totp_enabled;
  // Whether the renter's authenticator status has been resolved. 'unknown'
  // while loading, 'ready' once a status check succeeds, 'error' when it fails.
  // The app-wide prefetch (UserUpdateManager) usually resolves this before the
  // screen mounts, so we start 'ready' and never flash an enabled Edit button.
  const [authStatus, setAuthStatus] = useState<'unknown' | 'ready' | 'error'>(
    elevatedAuth.statusChecked ? 'ready' : 'unknown'
  );
  // isSelf only decides the copy/route (self vs. ?as_user_id proxy) — it does
  // NOT exempt the edit from step-up: editing your own contacts still requires
  // verification.
  const isSelf = member.user_id != null && member.user_id === user?.id;
  // The gate applies to self and proxy alike; it is only cleared once we *know*
  // 2FA is set up. A failed status check must not masquerade as "no
  // authenticator" (that would disable Edit for a user who already has one).
  const editBlocked = authStatus === 'ready' && totpEnabled === false;

  useElevatedAuthExpiryWatcher(elevatedAuth.expires_at);

  // Guards post-await state updates if the screen unmounts mid-request.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadAuthStatus = useCallback(async (): Promise<void> => {
    try {
      const res = await elevatedAuthService.getStatus();
      if (!mountedRef.current) {
        return;
      }
      dispatch(
        setElevatedAuth({
          verified: res.verified,
          expires_at: res.expires_at ?? undefined,
          totp_enabled: res.totp_enabled,
          method: res.method ?? null,
        })
      );
      setAuthStatus('ready');
    } catch {
      // Could not confirm the authenticator status — surface a retry instead of
      // assuming 2FA is missing.
      if (mountedRef.current) {
        setAuthStatus('error');
      }
    }
  }, [dispatch]);

  useEffect(() => {
    const fetchMemberTypes = async (): Promise<void> => {
      try {
        const res = await axios.get('household/household_member_type/');
        if (res.status === 200) {
          const sorted = (res.data as MemberType[]).sort((a, b) => {
            if (a.name.toLowerCase() === 'head of household') {
              return -1;
            }
            if (b.name.toLowerCase() === 'head of household') {
              return 1;
            }
            return 0;
          });
          setMemberTypes(sorted);
        }
      } catch {
        setAlertMessage({
          type: 'error',
          message: 'Error loading form data. Please try again.',
        });
      }
    };
    void fetchMemberTypes();
  }, []);

  // Seed the elevated-auth status so we know up front whether the renter can
  // edit another member's contacts (TOTP) or needs to set up an authenticator.
  // Skipped when the app-wide prefetch already resolved it.
  useEffect(() => {
    if (!elevatedAuth.statusChecked) {
      void loadAuthStatus();
    }
  }, [elevatedAuth.statusChecked, loadAuthStatus]);

  const memberIsCurrentHoH = member.is_head_of_household;

  const currentHoH = (household.cert_members ?? []).find(
    (m) => m.cert_member_id !== member.cert_member_id && m.is_head_of_household
  );

  const otherMembers: HouseholdMemberOption[] = (household.cert_members ?? [])
    .filter((m) => m.cert_member_id !== member.cert_member_id)
    .map((m) => ({
      id: m.cert_member_id,
      cert_member_id: m.cert_member_id,
      first_name: m.first_name ?? '',
      middle_name: m.middle_name,
      last_name: m.last_name ?? '',
    }));

  const nonHoHTypes = memberTypes.filter(
    (t) => t.name.toLowerCase() !== 'head of household'
  );

  const canChangeMemberType = hasOtherMembers && currentUserCanManage;

  const getMemberFullName = (m: CertMember): string => {
    const parts = [m.first_name, m.middle_name, m.last_name].filter(Boolean);
    return parts.join(' ').trim() || '—';
  };

  const colors = getRoleColors(member.member_type_name);
  // Use the currently selected type from the form (not the saved value) so the
  // contact section shows/hides reactively as the user changes the relationship.
  const isMinor = isMinorMemberType(
    formValues.member_type?.display_name ??
      formValues.member_type?.name ??
      member.member_type_name
  );
  const initials = [member.first_name, member.last_name]
    .filter((n): n is string => Boolean(n))
    .map((n) => n[0].toUpperCase())
    .join('');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formValues.first_name.trim()) {
      newErrors.first_name = 'First Name is required.';
    }
    if (!formValues.last_name.trim()) {
      newErrors.last_name = 'Last Name is required.';
    }
    if (!formValues.member_type) {
      newErrors.member_type = 'Select a valid choice.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const executeSubmit = async (
    demotedRole: MemberType | null,
    newHoH: HouseholdMemberOption | null
  ): Promise<void> => {
    setLoading(true);
    setAlertMessage(null);

    try {
      const newTypeIsHoH =
        formValues.member_type?.name?.toLowerCase() === 'head of household';

      // Always use v2 CertMemberViewSet endpoint.
      const payload: Record<string, unknown> = {
        first_name: formValues.first_name.trim(),
        last_name: formValues.last_name.trim(),
        middle_name: formValues.middle_name?.trim() ?? null,
        member_type_id: formValues.member_type?.id ?? undefined,
      };

      // Track the member that gains HoH (demotion flow) so we can update Redux.
      let promotedHoHMember: CertMember | undefined;

      // HoH demotion-with-replacement: patch the NEW HoH's cert_member with
      // is_hoh=true — v2 infers demotion of the old HoH automatically.
      if (memberIsCurrentHoH && !newTypeIsHoH && newHoH) {
        const newHoHFullMember = (household.cert_members ?? []).find(
          (m) => m.cert_member_id === newHoH.cert_member_id
        );
        if (newHoHFullMember?.cert_member_id != null) {
          await axios.patch(
            `household/cert_member/${newHoHFullMember.cert_member_id}/`,
            { is_hoh: true }
          );
          promotedHoHMember = newHoHFullMember;
          // Self-patch still updates name/type — but not is_hoh
        } else {
          // Fallback: old HoH self-patch won't trigger transfer, but at least
          // name/type are saved.
          payload.member_type_id = formValues.member_type?.id ?? undefined;
        }
      } else if (!memberIsCurrentHoH && newTypeIsHoH) {
        payload.is_hoh = true;
        if (demotedRole?.id != null) {
          payload.demoted_member_type_id = demotedRole.id;
        }
      }

      await axios.patch(
        `household/cert_member/${member.cert_member_id}/`,
        payload
      );

      // Save contact info (State A only) when the member had none and the user
      // entered new values. Runs in its own try-catch so a failure here does
      // not roll back the already-persisted profile / member-type changes above.
      let newEmails = member.emails ?? [];
      let newPhones = member.phones ?? [];
      let contactSaveFailed = false;
      if (!hasContact && (contactEmail.trim() || contactPhone.trim())) {
        const contactPatch: Record<string, unknown> = {};
        if (contactEmail.trim()) {
          contactPatch.email = contactEmail.trim().toLowerCase();
          const emailValue = contactEmail.trim().toLowerCase();
          const existing = newEmails.find(
            (e) => e.email.toLowerCase() === emailValue
          );
          if (existing) {
            newEmails = newEmails.map((e) =>
              e.email.toLowerCase() === emailValue
                ? { ...e, is_primary: true }
                : { ...e, is_primary: false }
            );
          } else {
            newEmails = [
              ...newEmails.map((e) => ({ ...e, is_primary: false })),
              { id: Date.now(), email: emailValue, is_primary: true },
            ];
          }
        }
        if (contactPhone.trim()) {
          contactPatch.phone_number = contactPhone.trim();
          newPhones = [
            ...newPhones.map((p) => ({ ...p, is_primary: false })),
            {
              id: Date.now() + 1,
              phone_number: contactPhone.trim(),
              is_primary: true,
            },
          ];
        }
        try {
          await axios.patch(
            `household/cert_member/${member.cert_member_id}/`,
            contactPatch
          );
        } catch (err) {
          // Profile changes already saved — only warn about contact.
          // Revert the optimistic arrays so Redux receives the original
          // contact state, not data that was never persisted.
          newEmails = member.emails ?? [];
          newPhones = member.phones ?? [];
          contactSaveFailed = true;
          // Surface the server's field error (e.g. "Enter a valid email
          // address.") so the reason is actionable, not just a retry.
          const specific =
            fieldErrorFromPayload(err, 'email') ??
            fieldErrorFromPayload(err, 'phone_number');
          setAlertMessage({
            type: 'warning',
            message: specific
              ? `Changes saved, but contact information could not be updated: ${specific}`
              : 'Changes saved, but contact information could not be updated. Please try again.',
          });
        }
      }

      const updatedMember: CertMember = {
        ...member,
        first_name: formValues.first_name.trim(),
        middle_name: formValues.middle_name?.trim() || null,
        last_name: formValues.last_name.trim(),
        member_type_id: formValues.member_type?.id ?? member.member_type_id,
        member_type_name:
          formValues.member_type?.display_name ??
          formValues.member_type?.name ??
          member.member_type_name,
        member_type_weight:
          formValues.member_type?.weight ?? member.member_type_weight,
        is_head_of_household: newTypeIsHoH,
        emails: newEmails,
        phones: newPhones,
      };

      const updatedMembers: CertMember[] = [updatedMember];

      // Demotion: newly promoted member inherits the HoH type from the member
      // being demoted (backend transfers the HoH member type).
      if (promotedHoHMember != null) {
        updatedMembers.push({
          ...promotedHoHMember,
          is_head_of_household: true,
          member_type_id: member.member_type_id,
          member_type_name: member.member_type_name,
          member_type_weight: member.member_type_weight,
        });
      }

      // Promotion: the previous HoH lost their flag. No refetch happens after
      // the optimistic update, so write the demoted type picked in the transfer
      // modal onto the old HoH's entry — consistent with what the backend saves
      // and what the cert_member_updated WS event carries.
      if (!memberIsCurrentHoH && newTypeIsHoH) {
        const oldHoH = (household.cert_members ?? []).find(
          (m) =>
            m.is_head_of_household && m.cert_member_id !== member.cert_member_id
        );
        if (oldHoH) {
          updatedMembers.push({
            ...oldHoH,
            is_head_of_household: false,
            member_type_id: demotedRole?.id ?? oldHoH.member_type_id,
            member_type_name:
              demotedRole?.display_name ??
              demotedRole?.name ??
              oldHoH.member_type_name,
            member_type_weight:
              demotedRole?.weight ?? oldHoH.member_type_weight,
          });
        }
      }

      // Stay on this screen when only the contact save failed so the alert
      // (with the server's reason) is visible and the value can be fixed.
      if (!contactSaveFailed) {
        onSubmit(updatedMembers);
      }
    } catch {
      setAlertMessage({
        type: 'error',
        message: 'Unable to save changes. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    const newTypeIsHoH =
      formValues.member_type?.name?.toLowerCase() === 'head of household';

    if (!memberIsCurrentHoH && newTypeIsHoH) {
      setTransferDemotedRole(null);
      setShowHoHTransferDialog(true);
      return;
    }

    if (memberIsCurrentHoH && !newTypeIsHoH) {
      setDemotionNewHoH(null);
      setShowHoHDemotionDialog(true);
      return;
    }

    await executeSubmit(null, null);
  };

  const handleTransferConfirm = async (): Promise<void> => {
    await executeSubmit(transferDemotedRole, null);
    setShowHoHTransferDialog(false);
  };

  const handleDemotionConfirm = async (): Promise<void> => {
    await executeSubmit(null, demotionNewHoH);
    setShowHoHDemotionDialog(false);
  };

  const contactEmailConflict = useMemo(
    () =>
      findMemberContactConflict(
        household.cert_members ?? [],
        contactEmail,
        null,
        member.cert_member_id
      ),
    [household.cert_members, contactEmail, member.cert_member_id]
  );

  const contactPhoneConflict = useMemo(
    () =>
      findMemberContactConflict(
        household.cert_members ?? [],
        null,
        contactPhone,
        member.cert_member_id
      ),
    [household.cert_members, contactPhone, member.cert_member_id]
  );

  const isFormValid =
    formValues.first_name.trim() !== '' &&
    formValues.last_name.trim() !== '' &&
    formValues.member_type !== null &&
    contactEmailConflict === null &&
    contactPhoneConflict === null;

  return (
    <Stack sx={{ height: '100%', ...fadeStyle, ...sx }}>
      {alertMessage && (
        <Alert
          severity={alertMessage.type}
          sx={{ mb: 2 }}
          onClose={() => setAlertMessage(null)}
        >
          {alertMessage.message}
        </Alert>
      )}

      <Stack gap={2} sx={{ pr: 1, flex: '1 1 100%', overflow: 'auto' }}>
        {/* [1] Member hero card */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 2,
            borderLeft: '4px solid',
            borderLeftColor: colors.accentColor,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              backgroundColor: colors.avatarBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography
              fontWeight={700}
              sx={{ color: colors.avatarText, fontSize: '1.1rem' }}
            >
              {initials || '?'}
            </Typography>
          </Box>

          <Stack gap={0.75} flex={1} sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>
              {getMemberFullName(member)}
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                px: 1,
                py: 0.25,
                borderRadius: 0.5,
                backgroundColor: colors.pillBg,
                alignSelf: 'flex-start',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  color: colors.pillText,
                }}
              >
                {member.member_type_name}
              </Typography>
            </Box>
            {!hasContact && !isMinor && (
              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                sx={{ mt: 0.5 }}
              >
                <WarningAmberIcon
                  sx={{ fontSize: 14, color: 'warning.dark' }}
                />
                <Typography
                  variant="caption"
                  color="warning.dark"
                  fontWeight={600}
                >
                  Please add contact information for this member.
                </Typography>
              </Stack>
            )}
          </Stack>
        </Paper>

        {/* [2a] Personal Information card */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
            Personal Information
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              size="small"
              fullWidth
              label="First Name"
              value={formValues.first_name}
              onChange={(e) =>
                setFormValues({ ...formValues, first_name: e.target.value })
              }
              error={Boolean(errors.first_name)}
              helperText={errors.first_name}
              required
            />
            <TextField
              size="small"
              fullWidth
              label="Middle Name"
              value={formValues.middle_name}
              onChange={(e) =>
                setFormValues({ ...formValues, middle_name: e.target.value })
              }
            />
            <TextField
              size="small"
              fullWidth
              label="Last Name"
              value={formValues.last_name}
              onChange={(e) =>
                setFormValues({ ...formValues, last_name: e.target.value })
              }
              error={Boolean(errors.last_name)}
              helperText={errors.last_name}
              required
            />
          </Stack>

          <Autocomplete
            fullWidth
            size="small"
            disabled={!canChangeMemberType}
            options={memberTypes}
            sx={{ mt: 2 }}
            getOptionLabel={(option) => {
              const isHoH = option?.name?.toLowerCase() === 'head of household';
              if (isHoH && !memberIsCurrentHoH) {
                return 'New Head of Household';
              }
              return option?.display_name ?? '';
            }}
            getOptionDisabled={(option) => {
              const isHoHOption =
                option?.name?.toLowerCase() === 'head of household';
              const memberIsDependent =
                member.member_type_name?.toLowerCase() === 'dependent';
              return isHoHOption && memberIsDependent;
            }}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            value={formValues.member_type}
            onChange={(_event, newValue) =>
              setFormValues({ ...formValues, member_type: newValue })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Relationship with Head of Household"
                error={Boolean(errors.member_type)}
                helperText={
                  !hasOtherMembers
                    ? "Relationship with Head of Household can't be changed when there are no other household members."
                    : !currentUserCanManage
                    ? 'Only the Head of Household or a Helper can change this.'
                    : errors.member_type
                }
                required
              />
            )}
          />
        </Paper>

        {/* [2b] Contact Information card — hidden for minor/dependent members */}
        {!isMinor &&
          (hasContact ? (
            isElevated && member.user_id != null ? (
              // Elevated (authenticator) session — show only the editable
              // contacts, no card, title, or Edit controls. Required for self
              // edits too.
              <AccountContactsEditor
                asUser={isSelf ? '' : `?as_user_id=${String(member.user_id)}`}
                targetUserId={member.user_id}
                ownerName={isSelf ? undefined : member.first_name?.trim()}
              />
            ) : (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{ mb: 1.5 }}
                >
                  Contact Information
                </Typography>

                <Stack gap={0.75}>
                  {(() => {
                    const primaryEmail = (member.emails ?? []).find(
                      (e) =>
                        e.is_primary && !e.email.endsWith('@prontohousing.io')
                    );
                    return primaryEmail ? (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <EmailIcon
                          sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {primaryEmail.email}
                        </Typography>
                      </Stack>
                    ) : null;
                  })()}
                  {(() => {
                    const primaryPhone = (member.phones ?? []).find(
                      (p) => p.is_primary
                    );
                    return primaryPhone ? (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <PhoneIcon
                          sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {formatPhoneNumber(
                            primaryPhone.phone_number
                              .replace(/\D/g, '')
                              .replace(/^1/, '')
                          )}
                        </Typography>
                      </Stack>
                    ) : null;
                  })()}
                </Stack>

                {member.user_id != null && (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={editBlocked}
                    sx={{
                      mt: 2,
                      backgroundColor: 'action.selected',
                      color: 'text.secondary',
                      boxShadow: 1,
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    startIcon={
                      <EditOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'text.disabled' }}
                      />
                    }
                    onClick={() => setShowContactEditor(true)}
                  >
                    <span>Edit Contact Information</span>
                  </Button>
                )}

                {editBlocked && (
                  <Box sx={{ mt: 2 }}>
                    <TwoFactorRequiredCard isSelf={isSelf} />
                  </Box>
                )}

                {authStatus === 'error' && (
                  <Alert
                    severity="warning"
                    sx={{ mt: 2 }}
                    action={
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => void loadAuthStatus()}
                      >
                        Retry
                      </Button>
                    }
                  >
                    Couldn&apos;t confirm your two-factor authentication status.
                    You can still try editing, or retry.
                  </Alert>
                )}

                {showContactEditor && (
                  <MemberContactEditor
                    member={member}
                    isSelf={isSelf}
                    statusKnown={authStatus === 'ready'}
                    onDismiss={() => setShowContactEditor(false)}
                  />
                )}
              </Paper>
            )
          ) : (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Contact Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Contact information is used to send this member important
                notifications, forms, and invitations to complete their
                certification.
              </Typography>

              <Stack gap={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="email@example.com"
                  error={Boolean(contactEmailConflict)}
                  helperText={
                    contactEmailConflict
                      ? 'This email is already used by another household member'
                      : undefined
                  }
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Phone Number"
                  type="tel"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="(555) 000-0000"
                  error={Boolean(contactPhoneConflict)}
                  helperText={
                    contactPhoneConflict
                      ? 'This phone number is already used by another household member'
                      : undefined
                  }
                />
              </Stack>

              <Alert
                severity="info"
                sx={{ mt: 2 }}
                icon={<InfoOutlinedIcon fontSize="small" />}
              >
                <Typography variant="caption">
                  This member&apos;s contact information is saved as soon as you
                  save your changes.
                </Typography>
              </Alert>
            </Paper>
          ))}

        {/* [2c] Remove Member — hidden for Head of Household */}
        {!memberIsCurrentHoH && (
          <Button
            fullWidth
            variant="outlined"
            size="small"
            sx={{ color: 'error.main', borderColor: 'error.main', mt: 1 }}
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(member);
            }}
            startIcon={<PersonRemoveIcon fontSize="small" />}
          >
            <span>Remove Member</span>
          </Button>
        )}
      </Stack>

      {/* [3] Action buttons */}
      <Stack sx={{ mt: 2 }}>
        <Button
          fullWidth
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !isFormValid}
          size="large"
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          <span>{loading ? 'Saving…' : 'Save changes'}</span>
        </Button>
      </Stack>

      <HoHTransferDialog
        open={showHoHTransferDialog}
        newHohName={`${formValues.first_name ?? ''} ${
          formValues.last_name ?? ''
        }`.trim()}
        currentHeadName={
          currentHoH
            ? `${currentHoH.first_name ?? ''} ${
                currentHoH.last_name ?? ''
              }`.trim()
            : 'Current Head of Household'
        }
        roleOptions={nonHoHTypes}
        value={transferDemotedRole}
        onChange={setTransferDemotedRole}
        loading={loading}
        onConfirm={handleTransferConfirm}
        onClose={() => setShowHoHTransferDialog(false)}
      />

      <HoHDemotionDialog
        open={showHoHDemotionDialog}
        currentHohName={`${formValues.first_name ?? ''} ${
          formValues.last_name ?? ''
        }`.trim()}
        newRoleName={formValues.member_type?.display_name ?? ''}
        memberOptions={otherMembers}
        value={demotionNewHoH}
        onChange={setDemotionNewHoH}
        loading={loading}
        onConfirm={handleDemotionConfirm}
        onClose={() => setShowHoHDemotionDialog(false)}
      />
    </Stack>
  );
};

export default EditHouseholdMemberScreen;
